import React, { useLayoutEffect, useRef } from 'react';
import { Canvas } from '@react-three/fiber';
import { OrbitControls } from '@react-three/drei';

export interface DiagramPoint { x: number; y: number; label?: string }
export interface DiagramSpec {
  type: 'right-triangle' | 'unit-circle' | 'coordinate-plane' | 'graph' | 'function-graph' | 'area-region' | 'triangle' | 'circle' | 'number-line' | 'vector' | 'box-plot' | 'bar-chart' | 'normal-distribution' | '3d-cube' | '3d-sphere' | '3d-cylinder' | '3d-cone' | '3d-rectangular-prism' | 'interactive-unit-circle' | 'interactive-coordinate-plane' | 'bearing' | 'locus' | 'scale-drawing' | 'bearing-elevation' | 'bearing-journey';
  caption?: string;
  label?: string;
  // right-triangle
  legs?: [number, number];
  showSquares?: boolean;
  // unit-circle
  angle?: number;
  showSin?: boolean;
  showCos?: boolean;
  showTan?: boolean;
  // coordinate-plane
  points?: DiagramPoint[];
  line?: { x1: number; y1: number; x2: number; y2: number; label?: string };
  xRange?: [number, number];
  yRange?: [number, number];
  equation?: string;
  expression?: string;
  function?: string;
  curve?: string;
  functions?: { equation?: string; expression?: string; label?: string; color?: string }[];
  xBounds?: [number, number];
  yBounds?: [number, number];
  verticalLines?: number[];
  shadeUnderCurve?: boolean;
  // triangle
  vertices?: DiagramPoint[];
  // circle
  center?: DiagramPoint;
  radius?: number;
  // number-line
  min?: number;
  max?: number;
  shade?: [number, number][];
  // vector
  origin?: DiagramPoint;
  end?: DiagramPoint;
  // box-plot
  q1?: number;
  median?: number;
  q3?: number;
  // bar-chart
  bars?: { label: string; value: number; color?: string }[];
  // normal-distribution
  mean?: number;
  stdDev?: number;
  range?: [number, number];
  shadeRange?: { from: number; to: number };
  // 3d shapes
  size?: number;
  width?: number;
  depth?: number;
  height?: number;
  // bearing
  bearing?: number;
  distance?: number;
  fromPoint?: { x: number; y: number; label?: string };
  toPoint?: { x: number; y: number; label?: string };
  // locus: from-point, from-line, between two lines, region intersection
  locusType?: 'from-point' | 'from-line' | 'two-points' | 'intersect' | 'region' | 'path';
  locusRadius?: number;
  locusRadius2?: number;
  locusLine?: { p1: { x: number; y: number }; p2: { x: number; y: number }; label?: string };
  locusLine2?: { p1: { x: number; y: number }; p2: { x: number; y: number } };
  locusPath?: { x: number; y: number }[];
  // scale-drawing
  scale?: number;
  scaleUnit?: string;
  // bearing-elevation (3D)
  elevation?: number;
  trueDistance?: number;
  // bearing-journey: multi-leg bearing path
  bearingJourney?: {
    origin: { label: string };
    legs: { bearing: number; distance: number; toLabel: string }[];
    scale?: number;
    scaleUnit?: string;
  };
}

const SVG_W = 400;
const SVG_H = 300;
const STROKE = '#6366f1';
const STROKE_2 = '#94a3b8';
const TEXT = '#0f172a';
const TEXT_LIGHT = '#64748b';
const FILL = '#eef2ff';

export const DiagramBlock = ({ spec }: { spec: DiagramSpec }) => {
  const normalizedSpec = normalizeDiagramSpec(spec);
  const { type, caption } = normalizedSpec;
  const captionEl = caption ? (
    <p className="text-[10px] text-center font-bold uppercase tracking-widest text-slate-500 dark:text-slate-500 mt-2">
      {caption}
    </p>
  ) : null;

  const isNonSvg = type.startsWith('3d-') || type.startsWith('interactive-');

  const svgContent = (() => {
    switch (type) {
      case 'right-triangle':
        return <RightTriangle spec={normalizedSpec} />;
      case 'unit-circle':
        return <UnitCircle spec={normalizedSpec} />;
      case 'coordinate-plane':
        return hasFunctionGraphData(normalizedSpec) ? <FunctionGraphDiagram spec={normalizedSpec} /> : <CoordinatePlane spec={normalizedSpec} />;
      case 'graph':
      case 'function-graph':
      case 'area-region':
        return <FunctionGraphDiagram spec={normalizedSpec} />;
      case 'triangle':
        return <Triangle spec={normalizedSpec} />;
      case 'circle':
        return <CircleDiagram spec={normalizedSpec} />;
      case 'number-line':
        return <NumberLine spec={normalizedSpec} />;
      case 'vector':
        return <VectorDiagram spec={normalizedSpec} />;
      case 'box-plot':
        return <BoxPlot spec={normalizedSpec} />;
      case 'bar-chart':
        return <BarChartDiagram spec={normalizedSpec} />;
      case 'normal-distribution':
        return <NormalDistribution spec={normalizedSpec} />;
      case 'bearing':
        return <BearingDiagram spec={normalizedSpec} />;
      case 'locus':
        return <LocusDiagram spec={normalizedSpec} />;
      case 'scale-drawing':
        return <ScaleDrawing spec={normalizedSpec} />;
      case 'bearing-elevation':
        return <BearingElevation spec={normalizedSpec} />;
      case 'bearing-journey':
        return <BearingJourney spec={normalizedSpec} />;
      // 3D and interactive components omitted for 2D generator
      case '3d-cube':
      case '3d-sphere':
      case '3d-cylinder':
      case '3d-cone':
      case '3d-rectangular-prism':
      case 'interactive-unit-circle':
      case 'interactive-coordinate-plane':
        console.warn('3D/Interactive not supported in this 2D diagram block');
        return <UnsupportedDiagram type={type} />;
      default:
        console.warn(`[DiagramBlock] Unknown diagram type: ${type}`);
        return <UnsupportedDiagram type={type} />;
    }
  })();

  if (isNonSvg) {
    return (
      <div className="my-3">
        <div className="rounded-lg border border-slate-200 dark:border-white/10 overflow-hidden bg-white dark:bg-[#0a1024]">
          {svgContent}
        </div>
        {captionEl}
      </div>
    );
  }

  return (
    <div className="my-3">
      <svg viewBox={`0 0 ${SVG_W} ${SVG_H}`} className="w-full h-auto rounded-lg border border-slate-200 dark:border-white/10 bg-white dark:bg-[#0a1024]">
        {svgContent}
      </svg>
      {captionEl}
    </div>
  );
};

const normalizeDiagramSpec = (spec: DiagramSpec): DiagramSpec => {
  const rawType = String((spec as any)?.type || 'coordinate-plane')
    .trim()
    .toLowerCase()
    .replace(/[,\s]+$/g, '')
    .replace(/_/g, '-');
  const aliases: Record<string, DiagramSpec['type']> = {
    cartesian: 'coordinate-plane',
    'cartesian-plane': 'coordinate-plane',
    plot: 'function-graph',
    function: 'function-graph',
    functiongraph: 'function-graph',
    graph: 'function-graph',
    region: 'area-region',
    area: 'area-region',
    'area-under-curve': 'area-region',
  };
  const type = aliases[rawType] || rawType;
  return { ...spec, type } as DiagramSpec;
};

const UnsupportedDiagram = ({ type }: { type: string }) => (
  <g>
    <rect x={28} y={40} width={SVG_W - 56} height={SVG_H - 80} rx={10} fill={FILL} fillOpacity={0.35} stroke={STROKE_2} strokeDasharray="5 4" />
    <text x={SVG_W / 2} y={SVG_H / 2 - 8} textAnchor="middle" fontSize={12} fill={TEXT} fontWeight={700}>
      Diagram type not supported
    </text>
    <text x={SVG_W / 2} y={SVG_H / 2 + 12} textAnchor="middle" fontSize={10} fill={TEXT_LIGHT}>
      {type || 'unknown'}
    </text>
  </g>
);

const RightTriangle = ({ spec }: { spec: DiagramSpec }) => {
  const [a, b] = spec.legs ?? [3, 4];
  const scale = Math.min((SVG_W - 80) / a, (SVG_H - 80) / b);
  const aw = a * scale;
  const bh = b * scale;
  const ox = 40;
  const oy = SVG_H - 40;
  const x1 = ox + aw;
  const y1 = oy;
  const x2 = ox;
  const y2 = oy - bh;
  const angleRad = Math.atan2(bh, aw);
  const angleDeg = (angleRad * 180) / Math.PI;
  return (
    <g>
      {spec.showSquares && (
        <>
          <rect x={ox} y={oy - 14} width={aw} height={14} fill={FILL} stroke={STROKE_2} strokeWidth={1} strokeDasharray="2 2" />
          <rect x={ox - 14} y={oy - bh} width={14} height={bh} fill={FILL} stroke={STROKE_2} strokeWidth={1} strokeDasharray="2 2" />
        </>
      )}
      <polygon points={`${ox},${oy} ${x1},${y1} ${x2},${y2}`} fill="none" stroke={STROKE} strokeWidth={2.5} strokeLinejoin="round" />
      <rect x={ox} y={oy - 10} width={10} height={10} fill="none" stroke={TEXT_LIGHT} strokeWidth={1.5} />
      <text x={ox + aw / 2} y={oy + 16} textAnchor="middle" fontSize={11} fill={TEXT} fontWeight={600}>{a}</text>
      <text x={ox - 20} y={oy - bh / 2} textAnchor="middle" fontSize={11} fill={TEXT} fontWeight={600}>{b}</text>
      <text x={(ox + x1 + x2) / 3} y={(oy + y1 + y2) / 3 - 6} textAnchor="middle" fontSize={11} fill={TEXT} fontWeight={600}>c = {Math.hypot(a, b).toFixed(2)}</text>
      <text x={ox + 8} y={oy - 8} fontSize={9} fill={TEXT_LIGHT}>{angleDeg.toFixed(0)}°</text>
    </g>
  );
};

const UnitCircle = ({ spec }: { spec: DiagramSpec }) => {
  const angle = ((spec.angle ?? 45) * Math.PI) / 180;
  const cx = SVG_W / 2;
  const cy = SVG_H / 2;
  const r = Math.min(SVG_W, SVG_H) / 2 - 30;
  const x = cx + r * Math.cos(angle);
  const y = cy - r * Math.sin(angle);
  const px = x;
  const py = cy;
  return (
    <g>
      <line x1={cx - r - 20} y1={cy} x2={cx + r + 20} y2={cy} stroke={STROKE_2} strokeWidth={1} />
      <line x1={cx} y1={cy + r + 20} x2={cx} y2={cy - r - 20} stroke={STROKE_2} strokeWidth={1} />
      <circle cx={cx} cy={cy} r={r} fill="none" stroke={STROKE} strokeWidth={2} />
      <line x1={cx} y1={cy} x2={x} y2={y} stroke={STROKE} strokeWidth={2} />
      {spec.showCos && <line x1={x} y1={y} x2={px} y2={py} stroke="#10b981" strokeWidth={2.5} />}
      {spec.showSin && <line x1={px} y1={py} x2={x} y2={y} stroke="#ef4444" strokeWidth={2.5} />}
      {spec.showTan && (
        <line x1={px} y1={cy} x2={px} y2={cy - r * Math.tan(angle)} stroke="#f59e0b" strokeWidth={2.5} strokeDasharray="3 2" />
      )}
      <circle cx={x} cy={y} r={3} fill={STROKE} />
      <text x={cx + 12} y={cy - 6} fontSize={11} fill={TEXT} fontWeight={600}>1</text>
      <text x={cx + 12} y={cy - r + 4} fontSize={11} fill={TEXT} fontWeight={600}>cos θ</text>
      <text x={cx + r - 4} y={cy + 14} fontSize={11} fill={TEXT} fontWeight={600}>sin θ</text>
      <text x={cx + 4} y={cy + 14} fontSize={10} fill={TEXT_LIGHT}>0</text>
      <text x={cx + 8} y={cy - 4} fontSize={10} fill={TEXT_LIGHT}>θ</text>
      <text x={cx + 4} y={cy - 8} fontSize={10} fill={TEXT_LIGHT}>{spec.angle ?? 45}°</text>
    </g>
  );
};

const graphExpression = (spec: DiagramSpec) =>
  spec.equation || spec.expression || spec.function || spec.curve || spec.functions?.[0]?.equation || spec.functions?.[0]?.expression || '';

const hasFunctionGraphData = (spec: DiagramSpec) =>
  Boolean(graphExpression(spec) || spec.shadeUnderCurve || spec.verticalLines?.length || spec.xBounds || spec.yBounds);

const evaluateGraphY = (expression: string, x: number) => {
  const cleaned = expression.toLowerCase().replace(/\s+/g, '');
  if (cleaned.includes('sqrt')) return Math.sqrt(Math.max(x, 0));
  if (cleaned.includes('x^2-4') || cleaned.includes('x²-4')) return x * x - 4;
  if (cleaned.includes('x^2') || cleaned.includes('x²')) return x * x;
  if (cleaned.includes('cos') && cleaned.includes('x')) return x * Math.cos(x * x);
  if (cleaned.includes('sin') && cleaned.includes('x')) return Math.sin(x);
  if (/^y?=?x$/.test(cleaned)) return x;
  return Math.sqrt(Math.max(x, 0));
};

const FunctionGraphDiagram = ({ spec }: { spec: DiagramSpec }) => {
  const expression = graphExpression(spec);
  const bounds = spec.shadeRange ? [spec.shadeRange.from, spec.shadeRange.to] as [number, number] : spec.xBounds;
  const [xMin, xMax] = spec.xRange ?? [Math.min(0, bounds?.[0] ?? -1), Math.max(5, (bounds?.[1] ?? 4) + 1)];
  const sampleXs = Array.from({ length: 121 }, (_, i) => xMin + (i / 120) * (xMax - xMin));
  const sampleYs = sampleXs.map((x) => evaluateGraphY(expression, x)).filter(Number.isFinite);
  const computedYMin = Math.min(0, ...sampleYs);
  const computedYMax = Math.max(1, ...sampleYs);
  const [yMin, yMax] = spec.yRange ?? spec.yBounds ?? [Math.floor(computedYMin) - 1, Math.ceil(computedYMax) + 1];
  const padX = 34;
  const padY = 22;
  const plotW = SVG_W - 2 * padX;
  const plotH = SVG_H - 2 * padY;
  const sx = (x: number) => padX + ((x - xMin) / (xMax - xMin)) * plotW;
  const sy = (y: number) => padY + ((yMax - y) / (yMax - yMin)) * plotH;
  const points = sampleXs
    .map((x) => ({ x, y: evaluateGraphY(expression, x) }))
    .filter((p) => Number.isFinite(p.y) && p.y >= yMin - 1 && p.y <= yMax + 1);
  const curvePath = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${sx(p.x)},${sy(p.y)}`).join(' ');
  const shadeFrom = bounds?.[0] ?? xMin;
  const shadeTo = bounds?.[1] ?? xMax;
  const shadedPoints = points.filter((p) => p.x >= shadeFrom && p.x <= shadeTo);
  const shadePath = shadedPoints.length > 1
    ? `M ${sx(shadedPoints[0].x)},${sy(0)} L ${shadedPoints.map((p) => `${sx(p.x)},${sy(p.y)}`).join(' L ')} L ${sx(shadedPoints[shadedPoints.length - 1].x)},${sy(0)} Z`
    : '';
  const verticals = spec.verticalLines ?? (bounds ? [bounds[0], bounds[1]] : []);
  const xStep = Math.max(1, Math.ceil((xMax - xMin) / 8));
  const yStep = Math.max(1, Math.ceil((yMax - yMin) / 6));
  const xTicks = Array.from({ length: Math.floor((xMax - xMin) / xStep) + 1 }, (_, i) => xMin + i * xStep);
  const yTicks = Array.from({ length: Math.floor((yMax - yMin) / yStep) + 1 }, (_, i) => yMin + i * yStep);
  const yAxisX = xMin <= 0 && xMax >= 0 ? sx(0) : padX;
  const xAxisY = yMin <= 0 && yMax >= 0 ? sy(0) : padY + plotH;

  return (
    <g>
      <rect x={padX} y={padY} width={plotW} height={plotH} fill="#fafafa" className="dark:fill-white/[0.02]" stroke={STROKE_2} strokeWidth={1} />
      {xTicks.map((xv, i) => {
        return <line key={`fgx${i}`} x1={sx(xv)} y1={padY} x2={sx(xv)} y2={padY + plotH} stroke={STROKE_2} strokeWidth={0.45} strokeDasharray="2 3" />;
      })}
      {yTicks.map((yv, i) => {
        return <line key={`fgy${i}`} x1={padX} y1={sy(yv)} x2={padX + plotW} y2={sy(yv)} stroke={STROKE_2} strokeWidth={0.45} strokeDasharray="2 3" />;
      })}
      {xMin <= 0 && xMax >= 0 && <line x1={sx(0)} y1={padY} x2={sx(0)} y2={padY + plotH} stroke={TEXT_LIGHT} strokeWidth={1.4} />}
      {yMin <= 0 && yMax >= 0 && <line x1={padX} y1={sy(0)} x2={padX + plotW} y2={sy(0)} stroke={TEXT_LIGHT} strokeWidth={1.4} />}
      {xTicks.map((xv, i) => (
        <g key={`fgtx${i}`}>
          <line x1={sx(xv)} y1={xAxisY - 4} x2={sx(xv)} y2={xAxisY + 4} stroke={TEXT_LIGHT} strokeWidth={0.8} />
          <text x={sx(xv)} y={Math.min(SVG_H - 8, xAxisY + 16)} textAnchor="middle" fontSize={8} fill={TEXT_LIGHT}>{xv}</text>
        </g>
      ))}
      {yTicks.map((yv, i) => (
        <g key={`fgty${i}`}>
          <line x1={yAxisX - 4} y1={sy(yv)} x2={yAxisX + 4} y2={sy(yv)} stroke={TEXT_LIGHT} strokeWidth={0.8} />
          <text x={Math.max(8, yAxisX - 8)} y={sy(yv) + 3} textAnchor="end" fontSize={8} fill={TEXT_LIGHT}>{yv}</text>
        </g>
      ))}
      {shadePath && <path d={shadePath} fill={STROKE} fillOpacity={0.18} stroke="none" />}
      {curvePath && <path d={curvePath} fill="none" stroke={STROKE} strokeWidth={2.5} />}
      {verticals.map((x, i) => (
        <g key={`v${i}`}>
          <line x1={sx(x)} y1={xAxisY} x2={sx(x)} y2={sy(evaluateGraphY(expression, x))} stroke="#ef4444" strokeWidth={1.8} strokeDasharray="4 3" />
          <text x={sx(x) + 4} y={xAxisY - 8} fontSize={9} fill="#ef4444" fontWeight={700}>x = {x}</text>
        </g>
      ))}
      <text x={SVG_W - padX - 4} y={xAxisY - 6} textAnchor="end" fontSize={9} fill={TEXT_LIGHT}>x</text>
      <text x={yAxisX + 6} y={padY + 12} fontSize={9} fill={TEXT_LIGHT}>y</text>
      <text x={padX + 8} y={padY + 14} fontSize={10} fill={STROKE} fontWeight={700}>{expression}</text>
      {bounds && (
        <text x={padX + plotW / 2} y={SVG_H - 8} textAnchor="middle" fontSize={9} fill={TEXT_LIGHT}>
          {`shaded interval: ${bounds[0]} <= x <= ${bounds[1]}`}
        </text>
      )}
    </g>
  );
};

const CoordinatePlane = ({ spec }: { spec: DiagramSpec }) => {
  const [xMin, xMax] = spec.xRange ?? [-5, 5];
  const [yMin, yMax] = spec.yRange ?? [-5, 5];
  const padX = 30;
  const padY = 20;
  const plotW = SVG_W - 2 * padX;
  const plotH = SVG_H - 2 * padY;
  const sx = (x: number) => padX + ((x - xMin) / (xMax - xMin)) * plotW;
  const sy = (y: number) => padY + ((yMax - y) / (yMax - yMin)) * plotH;
  const xStep = Math.max(1, Math.ceil((xMax - xMin) / 10));
  const yStep = Math.max(1, Math.ceil((yMax - yMin) / 10));
  return (
    <g>
      <rect x={padX} y={padY} width={plotW} height={plotH} fill="#fafafa" className="dark:fill-white/[0.02]" stroke={STROKE_2} strokeWidth={1} />
      {Array.from({ length: Math.floor((xMax - xMin) / xStep) + 1 }, (_, i) => {
        const xv = xMin + i * xStep;
        if (xv === 0) return null;
        return <line key={`gx${i}`} x1={sx(xv)} y1={padY} x2={sx(xv)} y2={padY + plotH} stroke={STROKE_2} strokeWidth={0.5} strokeDasharray="2 3" />;
      })}
      {Array.from({ length: Math.floor((yMax - yMin) / yStep) + 1 }, (_, i) => {
        const yv = yMin + i * yStep;
        if (yv === 0) return null;
        return <line key={`gy${i}`} x1={padX} y1={sy(yv)} x2={padX + plotW} y2={sy(yv)} stroke={STROKE_2} strokeWidth={0.5} strokeDasharray="2 3" />;
      })}
      <line x1={sx(0)} y1={padY} x2={sx(0)} y2={padY + plotH} stroke={TEXT_LIGHT} strokeWidth={1.5} />
      <line x1={padX} y1={sy(0)} x2={padX + plotW} y2={sy(0)} stroke={TEXT_LIGHT} strokeWidth={1.5} />
      {spec.line && (
        <line x1={sx(spec.line.x1)} y1={sy(spec.line.y1)} x2={sx(spec.line.x2)} y2={sy(spec.line.y2)} stroke={STROKE} strokeWidth={2.5} />
      )}
      {spec.line?.label && (
        <text x={sx(spec.line.x2) + 4} y={sy(spec.line.y2) - 4} fontSize={10} fill={STROKE} fontWeight={600}>{spec.line.label}</text>
      )}
      {(spec.points ?? []).map((p, i) => (
        <g key={i}>
          <circle cx={sx(p.x)} cy={sy(p.y)} r={3.5} fill={STROKE} />
          {p.label && <text x={sx(p.x) + 6} y={sy(p.y) - 6} fontSize={10} fill={TEXT} fontWeight={600}>{p.label}</text>}
        </g>
      ))}
    </g>
  );
};

const Triangle = ({ spec }: { spec: DiagramSpec }) => {
  const verts = spec.vertices ?? [{ x: 0, y: 0, label: 'A' }, { x: 4, y: 0, label: 'B' }, { x: 1, y: 3, label: 'C' }];
  const xs = verts.map(v => v.x);
  const ys = verts.map(v => v.y);
  const xMin = Math.min(...xs) - 1;
  const xMax = Math.max(...xs) + 1;
  const yMin = Math.min(...ys) - 1;
  const yMax = Math.max(...ys) + 1;
  const padX = 30;
  const padY = 30;
  const plotW = SVG_W - 2 * padX;
  const plotH = SVG_H - 2 * padY;
  const sx = (x: number) => padX + ((x - xMin) / (xMax - xMin)) * plotW;
  const sy = (y: number) => padY + ((yMax - y) / (yMax - yMin)) * plotH;
  return (
    <g>
      <polygon
        points={verts.map(v => `${sx(v.x)},${sy(v.y)}`).join(' ')}
        fill={FILL}
        fillOpacity={0.5}
        stroke={STROKE}
        strokeWidth={2.5}
        strokeLinejoin="round"
      />
      {verts.map((v, i) => (
        <g key={i}>
          <circle cx={sx(v.x)} cy={sy(v.y)} r={3.5} fill={STROKE} />
          <text x={sx(v.x) + (i === 0 ? -12 : i === 1 ? 8 : 4)} y={sy(v.y) + (i === 0 ? 14 : i === 1 ? 14 : -8)} fontSize={12} fill={TEXT} fontWeight={700}>{v.label}</text>
        </g>
      ))}
    </g>
  );
};

const CircleDiagram = ({ spec }: { spec: DiagramSpec }) => {
  const cx = SVG_W / 2;
  const cy = SVG_H / 2;
  const r = Math.min(SVG_W, SVG_H) / 2 - 30;
  return (
    <g>
      <circle cx={cx} cy={cy} r={r} fill="none" stroke={STROKE} strokeWidth={2.5} />
      <circle cx={cx} cy={cy} r={3} fill={TEXT_LIGHT} />
      <line x1={cx} y1={cy} x2={cx + r} y2={cy} stroke="#ef4444" strokeWidth={2} />
      <text x={cx + r / 2} y={cy - 6} textAnchor="middle" fontSize={11} fill="#ef4444" fontWeight={600}>r = {spec.radius ?? r.toFixed(1)}</text>
      <text x={cx + 4} y={cy + 12} fontSize={10} fill={TEXT_LIGHT}>O</text>
    </g>
  );
};

const NumberLine = ({ spec }: { spec: DiagramSpec }) => {
  const min = spec.min ?? 0;
  const max = spec.max ?? 10;
  const padX = 30;
  const lineY = SVG_H / 2;
  const lineW = SVG_W - 2 * padX;
  const sx = (x: number) => padX + ((x - min) / (max - min)) * lineW;
  const range = max - min;
  const tickStep = range <= 10 ? 1 : range <= 20 ? 2 : range <= 50 ? 5 : 10;
  return (
    <g>
      <line x1={padX} y1={lineY} x2={SVG_W - padX} y2={lineY} stroke={TEXT_LIGHT} strokeWidth={1.5} />
      <polygon points={`${padX - 6},${lineY - 4} ${padX - 6},${lineY + 4} ${padX},${lineY}`} fill={TEXT_LIGHT} />
      <polygon points={`${SVG_W - padX + 6},${lineY - 4} ${SVG_W - padX + 6},${lineY + 4} ${SVG_W - padX},${lineY}`} fill={TEXT_LIGHT} />
      {(spec.shade ?? []).map(([a, b], i) => (
        <line key={`s${i}`} x1={sx(a)} y1={lineY - 6} x2={sx(b)} y2={lineY - 6} stroke={STROKE} strokeWidth={6} strokeLinecap="round" />
      ))}
      {Array.from({ length: Math.floor(range / tickStep) + 1 }, (_, i) => {
        const xv = min + i * tickStep;
        return (
          <g key={`t${i}`}>
            <line x1={sx(xv)} y1={lineY - 4} x2={sx(xv)} y2={lineY + 4} stroke={TEXT_LIGHT} strokeWidth={1} />
            <text x={sx(xv)} y={lineY + 16} textAnchor="middle" fontSize={9} fill={TEXT_LIGHT}>{xv}</text>
          </g>
        );
      })}
      {(spec.points ?? []).map((p, i) => (
        <g key={`p${i}`}>
          <circle cx={sx(p.x)} cy={lineY} r={5} fill={STROKE} />
          {p.label && <text x={sx(p.x)} y={lineY - 12} textAnchor="middle" fontSize={10} fill={TEXT} fontWeight={700}>{p.label}</text>}
        </g>
      ))}
    </g>
  );
};

const VectorDiagram = ({ spec }: { spec: DiagramSpec }) => {
  const cx = SVG_W / 2;
  const cy = SVG_H / 2;
  const scale = 40;
  const ex = cx + (spec.end?.x ?? 3) * scale;
  const ey = cy - (spec.end?.y ?? 4) * scale;
  return (
    <g>
      <line x1={20} y1={cy} x2={SVG_W - 20} y2={cy} stroke={STROKE_2} strokeWidth={1} />
      <line x1={cx} y1={SVG_H - 20} x2={cx} y2={20} stroke={STROKE_2} strokeWidth={1} />
      <line x1={cx} y1={cy} x2={ex} y2={ey} stroke={STROKE} strokeWidth={2.5} />
      <polygon
        points={`${ex},${ey} ${ex - 8 * Math.cos(Math.atan2(cy - ey, ex - cx) - 0.4)},${ey - 8 * Math.sin(Math.atan2(cy - ey, ex - cx) - 0.4)} ${ex - 8 * Math.cos(Math.atan2(cy - ey, ex - cx) + 0.4)},${ey - 8 * Math.sin(Math.atan2(cy - ey, ex - cx) + 0.4)}`}
        fill={STROKE}
      />
      <circle cx={cx} cy={cy} r={3} fill={TEXT_LIGHT} />
      {spec.label && <text x={ex + 8} y={ey - 4} fontSize={11} fill={TEXT} fontWeight={700}>{spec.label}</text>}
    </g>
  );
};

const BoxPlot = ({ spec }: { spec: DiagramSpec }) => {
  const min = spec.min ?? 0;
  const max = spec.max ?? 10;
  const q1 = spec.q1 ?? 3;
  const med = spec.median ?? 5;
  const q3 = spec.q3 ?? 7;
  const padX = 50;
  const lineY = SVG_H / 2;
  const lineW = SVG_W - 2 * padX;
  const sx = (x: number) => padX + ((x - min) / (max - min)) * lineW;
  return (
    <g>
      <line x1={padX} y1={lineY} x2={SVG_W - padX} y2={lineY} stroke={TEXT_LIGHT} strokeWidth={1.5} />
      <line x1={padX} y1={lineY} x2={padX} y2={lineY} stroke={TEXT_LIGHT} strokeWidth={1.5} />
      <line x1={sx(min)} y1={lineY - 18} x2={sx(min)} y2={lineY + 18} stroke={TEXT_LIGHT} strokeWidth={1.5} />
      <line x1={sx(max)} y1={lineY - 18} x2={sx(max)} y2={lineY + 18} stroke={TEXT_LIGHT} strokeWidth={1.5} />
      <line x1={sx(min)} y1={lineY} x2={sx(q1)} y2={lineY} stroke={STROKE} strokeWidth={2} />
      <line x1={sx(q3)} y1={lineY} x2={sx(max)} y2={lineY} stroke={STROKE} strokeWidth={2} />
      <rect x={sx(q1)} y={lineY - 30} width={sx(q3) - sx(q1)} height={60} fill={FILL} stroke={STROKE} strokeWidth={2} />
      <line x1={sx(med)} y1={lineY - 30} x2={sx(med)} y2={lineY + 30} stroke="#ef4444" strokeWidth={3} />
      <text x={sx(min)} y={lineY + 36} textAnchor="middle" fontSize={9} fill={TEXT_LIGHT}>min {min}</text>
      <text x={sx(q1)} y={lineY + 36} textAnchor="middle" fontSize={9} fill={TEXT_LIGHT}>Q1 {q1}</text>
      <text x={sx(med)} y={lineY + 36} textAnchor="middle" fontSize={9} fill="#ef4444" fontWeight={700}>med {med}</text>
      <text x={sx(q3)} y={lineY + 36} textAnchor="middle" fontSize={9} fill={TEXT_LIGHT}>Q3 {q3}</text>
      <text x={sx(max)} y={lineY + 36} textAnchor="middle" fontSize={9} fill={TEXT_LIGHT}>max {max}</text>
    </g>
  );
};

const BarChartDiagram = ({ spec }: { spec: DiagramSpec }) => {
  const bars = spec.bars ?? [{ label: 'A', value: 5 }, { label: 'B', value: 8 }];
  const maxV = Math.max(...bars.map(b => b.value));
  const padX = 30;
  const padY = 30;
  const plotW = SVG_W - 2 * padX;
  const plotH = SVG_H - 2 * padY;
  const barW = (plotW / bars.length) * 0.7;
  const gap = (plotW / bars.length) * 0.3;
  return (
    <g>
      <line x1={padX} y1={padY + plotH} x2={SVG_W - padX} y2={padY + plotH} stroke={TEXT_LIGHT} strokeWidth={1.5} />
      <line x1={padX} y1={padY} x2={padX} y2={padY + plotH} stroke={TEXT_LIGHT} strokeWidth={1.5} />
      {bars.map((b, i) => {
        const h = (b.value / maxV) * plotH;
        const x = padX + i * (barW + gap) + gap / 2;
        const y = padY + plotH - h;
        return (
          <g key={i}>
            <rect x={x} y={y} width={barW} height={h} fill={b.color ?? STROKE} />
            <text x={x + barW / 2} y={y - 4} textAnchor="middle" fontSize={10} fill={TEXT} fontWeight={700}>{b.value}</text>
            <text x={x + barW / 2} y={padY + plotH + 14} textAnchor="middle" fontSize={10} fill={TEXT_LIGHT}>{b.label}</text>
          </g>
        );
      })}
    </g>
  );
};

const NormalDistribution = ({ spec }: { spec: DiagramSpec }) => {
  const mean = spec.mean ?? 0;
  const std = spec.stdDev ?? 1;
  const [xMin, xMax] = spec.range ?? [mean - 4 * std, mean + 4 * std];
  const padX = 30;
  const padY = 30;
  const plotW = SVG_W - 2 * padX;
  const plotH = SVG_H - 2 * padY;
  const sx = (x: number) => padX + ((x - xMin) / (xMax - xMin)) * plotW;
  const peak = 1 / (std * Math.sqrt(2 * Math.PI));
  const maxY = peak;
  const sy = (y: number) => padY + plotH - (y / maxY) * plotH * 0.85;

  const points: string[] = [];
  const N = 100;
  for (let i = 0; i <= N; i++) {
    const x = xMin + (i / N) * (xMax - xMin);
    const y = (1 / (std * Math.sqrt(2 * Math.PI))) * Math.exp(-((x - mean) ** 2) / (2 * std * std));
    points.push(`${sx(x)},${sy(y)}`);
  }

  const pathData = `M ${sx(xMin)},${sy(0)} L ${points.join(' L ')} L ${sx(xMax)},${sy(0)} Z`;

  return (
    <g>
      <line x1={padX} y1={padY + plotH} x2={SVG_W - padX} y2={padY + plotH} stroke={TEXT_LIGHT} strokeWidth={1.5} />
      <path d={pathData} fill={FILL} stroke={STROKE} strokeWidth={2} />
      {spec.shadeRange && (
        <ShadedRegion
          xMin={xMin}
          xMax={xMax}
          shadeFrom={spec.shadeRange.from}
          shadeTo={spec.shadeRange.to}
          mean={mean}
          std={std}
          maxY={maxY}
          plotH={plotH}
          padX={padX}
          padY={padY}
          plotW={plotW}
        />
      )}
      <line x1={sx(mean)} y1={padY + plotH} x2={sx(mean)} y2={sy(maxY) + 4} stroke={TEXT_LIGHT} strokeWidth={1.5} strokeDasharray="2 2" />
      <text x={sx(mean)} y={padY + plotH + 14} textAnchor="middle" fontSize={9} fill={TEXT_LIGHT}>μ={mean}</text>
      <text x={sx(mean + std)} y={padY + plotH + 28} textAnchor="middle" fontSize={9} fill={TEXT_LIGHT}>σ={std}</text>
    </g>
  );
};

const ShadedRegion = ({ xMin, xMax, shadeFrom, shadeTo, mean, std, maxY, plotH, padX, padY, plotW }: {
  xMin: number; xMax: number; shadeFrom: number; shadeTo: number; mean: number; std: number; maxY: number; plotH: number; padX: number; padY: number; plotW: number;
}) => {
  const sx = (x: number) => padX + ((x - xMin) / (xMax - xMin)) * plotW;
  const sy = (y: number) => padY + plotH - (y / maxY) * plotH * 0.85;
  const points: string[] = [];
  const N = 50;
  const a = Math.max(xMin, shadeFrom);
  const b = Math.min(xMax, shadeTo);
  for (let i = 0; i <= N; i++) {
    const x = a + (i / N) * (b - a);
    const y = (1 / (std * Math.sqrt(2 * Math.PI))) * Math.exp(-((x - mean) ** 2) / (2 * std * std));
    points.push(`${sx(x)},${sy(y)}`);
  }
  if (points.length < 2) return null;
  const path = `M ${sx(a)},${sy(0)} L ${points.join(' L ')} L ${sx(b)},${sy(0)} Z`;
  return <path d={path} fill={STROKE} fillOpacity={0.4} />;
};

// === BEARING DIAGRAM ===
// A point P with a North arrow and a line at the given bearing (degrees clockwise from North).
// Distance labels the line. Standard 3-digit bearing notation: 045°, 120°, etc.
const BearingDiagram = ({ spec }: { spec: DiagramSpec }) => {
  const bearing = spec.bearing ?? 45;
  const dist = spec.distance ?? 5;
  const from = spec.fromPoint ?? { x: 0, y: 0, label: 'P' };
  const to = spec.toPoint;
  // convert bearing to math angle: 0° = up (North), clockwise positive
  const rad = (bearing * Math.PI) / 180;
  const len = 110;
  const cx = 200;
  const cy = 150;
  const endX = cx + len * Math.sin(rad);
  const endY = cy - len * Math.cos(rad);
  const a1 = rad - Math.PI / 2;
  const a2 = a1 + Math.PI;
  const arcR = 28;
  const arcEndX = cx + arcR * Math.sin(a1);
  const arcEndY = cy - arcR * Math.cos(a1);
  const arcStartX = cx + arcR * Math.sin(a2);
  const arcStartY = cy - arcR * Math.cos(a2);
  const largeArc = bearing > 180 ? 1 : 0;
  return (
    <g>
      <text x={cx} y={18} textAnchor="middle" fontSize={9} fill={TEXT_LIGHT} fontWeight={700}>N</text>
      <line x1={cx} y1={28} x2={cx} y2={48} stroke={TEXT_LIGHT} strokeWidth={1.2} markerEnd="url(#arrowNorth)" />
      <text x={cx + 12} y={40} fontSize={8} fill={TEXT_LIGHT}>↑</text>
      <line x1={cx} y1={cy} x2={endX} y2={endY} stroke={STROKE} strokeWidth={2} markerEnd="url(#arrowBearing)" />
      <path d={`M ${arcStartX},${arcStartY} A ${arcR},${arcR} 0 ${largeArc} 1 ${arcEndX},${arcEndY}`} fill="none" stroke="#ef4444" strokeWidth={1.2} />
      <text x={cx + 38 * Math.sin(rad / 2) + 10} y={cy - 38 * Math.cos(rad / 2) + 4} fontSize={9} fill="#ef4444" fontWeight={700}>{String(bearing).padStart(3, '0')}°</text>
      <text x={(cx + endX) / 2 + 8 * Math.cos(rad)} y={(cy + endY) / 2 - 8 * Math.sin(rad)} fontSize={8} fill={STROKE} fontWeight={600}>{dist} km</text>
      <circle cx={cx} cy={cy} r={3} fill={STROKE} />
      <text x={cx - 6} y={cy + 4} fontSize={9} fill={TEXT} fontWeight={700}>{from.label || 'P'}</text>
      {to && (
        <>
          <circle cx={endX} cy={endY} r={3} fill={STROKE} />
          <text x={endX + 6} y={endY + 4} fontSize={9} fill={TEXT} fontWeight={700}>{to.label || 'Q'}</text>
        </>
      )}
      <defs>
        <marker id="arrowNorth" viewBox="0 0 10 10" refX="5" refY="5" markerWidth="6" markerHeight="6" orient="auto">
          <path d="M 0 0 L 10 5 L 0 10 z" fill={TEXT_LIGHT} />
        </marker>
        <marker id="arrowBearing" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="7" markerHeight="7" orient="auto">
          <path d="M 0 0 L 10 5 L 0 10 z" fill={STROKE} />
        </marker>
      </defs>
    </g>
  );
};

// === LOCUS DIAGRAM ===
// Shows the region/set of points satisfying a geometric condition.
// Types: from-point (circle), from-line (parallel band), two-points (ellipse / intersection),
// intersect (overlap of two regions), region (custom polygon), path (custom).
const LocusDiagram = ({ spec }: { spec: DiagramSpec }) => {
  const ltype = spec.locusType ?? 'from-point';
  const cx = 200;
  const cy = 150;
  if (ltype === 'from-point' && spec.locusRadius != null) {
    const r = spec.locusRadius * 25;
    return (
      <g>
        <circle cx={cx} cy={cy} r={3} fill={STROKE} />
        <text x={cx + 6} y={cy - 6} fontSize={9} fill={TEXT} fontWeight={700}>{spec.fromPoint?.label || 'A'}</text>
        <circle cx={cx} cy={cy} r={r} fill="none" stroke={STROKE} strokeWidth={1.5} strokeDasharray="4 3" />
        <circle cx={cx} cy={cy} r={r} fill={STROKE} fillOpacity={0.15} />
        <text x={cx} y={cy + 5} textAnchor="middle" fontSize={9} fill={STROKE} fontWeight={700}>r = {spec.locusRadius}</text>
        <text x={SVG_W / 2} y={SVG_H - 12} textAnchor="middle" fontSize={9} fill={TEXT_LIGHT}>Locus: points within {spec.locusRadius} units of A</text>
      </g>
    );
  }
  if (ltype === 'from-line' && spec.locusLine && spec.locusRadius != null) {
    const { p1, p2 } = spec.locusLine;
    const pad = 30;
    const dx = p2.x - p1.x, dy = p2.y - p1.y;
    const len = Math.sqrt(dx * dx + dy * dy);
    const ux = -dy / len, uy = dx / len;
    const r = spec.locusRadius * 20;
    const o1 = { x: p1.x + ux * r, y: p1.y + uy * r };
    const o2 = { x: p2.x + ux * r, y: p2.y + uy * r };
    const o3 = { x: p2.x - ux * r, y: p2.y - uy * r };
    const o4 = { x: p1.x - ux * r, y: p1.y - uy * r };
    return (
      <g>
        <line x1={p1.x} y1={p1.y} x2={p2.x} y2={p2.y} stroke={STROKE} strokeWidth={1.5} />
        <text x={(p1.x + p2.x) / 2} y={(p1.y + p2.y) / 2 - 6} textAnchor="middle" fontSize={9} fill={STROKE} fontWeight={700}>{spec.locusLine.label || 'L'}</text>
        <polygon points={`${o1.x},${o1.y} ${o2.x},${o2.y} ${o3.x},${o3.y} ${o4.x},${o4.y}`} fill={STROKE} fillOpacity={0.15} stroke={STROKE} strokeWidth={1} strokeDasharray="3 3" />
        <text x={SVG_W / 2} y={SVG_H - 12} textAnchor="middle" fontSize={9} fill={TEXT_LIGHT}>Locus: points within {spec.locusRadius} of line {spec.locusLine.label || 'L'}</text>
      </g>
    );
  }
  if (ltype === 'intersect' && spec.locusRadius != null && spec.locusRadius2 != null) {
    const r1 = spec.locusRadius * 25;
    const r2 = spec.locusRadius2 * 25;
    const c1 = spec.fromPoint ?? { x: 140, y: 150 };
    const c2 = spec.toPoint ?? { x: 260, y: 150 };
    return (
      <g>
        <circle cx={c1.x} cy={c1.y} r={r1} fill={STROKE} fillOpacity={0.1} stroke={STROKE} strokeWidth={1.5} strokeDasharray="4 3" />
        <circle cx={c2.x} cy={c2.y} r={r2} fill="#ef4444" fillOpacity={0.1} stroke="#ef4444" strokeWidth={1.5} strokeDasharray="4 3" />
        <circle cx={c1.x} cy={c1.y} r={3} fill={STROKE} />
        <circle cx={c2.x} cy={c2.y} r={3} fill="#ef4444" />
        <text x={c1.x + 6} y={c1.y - 6} fontSize={9} fill={STROKE} fontWeight={700}>{c1.label || 'A'}</text>
        <text x={c2.x + 6} y={c2.y - 6} fontSize={9} fill="#ef4444" fontWeight={700}>{c2.label || 'B'}</text>
        <text x={SVG_W / 2} y={SVG_H - 12} textAnchor="middle" fontSize={9} fill={TEXT_LIGHT}>Intersection: points within {spec.locusRadius} of {c1.label || 'A'} AND {spec.locusRadius2} of {c2.label || 'B'}</text>
      </g>
    );
  }
  if (ltype === 'path' && spec.locusPath && spec.locusPath.length >= 2) {
    const pathStr = 'M ' + spec.locusPath.map(p => `${p.x},${p.y}`).join(' L ') + ' Z';
    return (
      <g>
        <polygon points={spec.locusPath.map(p => `${p.x},${p.y}`).join(' ')} fill={STROKE} fillOpacity={0.2} stroke={STROKE} strokeWidth={1.5} strokeDasharray="4 3" />
        <text x={SVG_W / 2} y={SVG_H - 12} textAnchor="middle" fontSize={9} fill={TEXT_LIGHT}>Locus path</text>
      </g>
    );
  }
  return <text x={SVG_W / 2} y={SVG_H / 2} textAnchor="middle" fontSize={10} fill={TEXT_LIGHT}>Locus: specify type and parameters</text>;
};

// === SCALE DRAWING ===
// Renders a diagram to scale with a scale bar showing the real-world distance.
const ScaleDrawing = ({ spec }: { spec: DiagramSpec }) => {
  const scaleFactor = spec.scale ?? 1;
  const unit = spec.scaleUnit ?? 'm';
  const from = spec.fromPoint ?? { x: 60, y: 220, label: 'A' };
  const to = spec.toPoint ?? { x: 340, y: 220, label: 'B' };
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  const pixelDist = Math.sqrt(dx * dx + dy * dy);
  const realDist = pixelDist / scaleFactor;
  const barLen = 80;
  const barReal = barLen / scaleFactor;
  const barX = SVG_W - barLen - 30;
  const barY = SVG_H - 20;
  return (
    <g>
      {spec.vertices?.map((v, i) => {
        if (i === 0) return null;
        const prev = spec.vertices![i - 1];
        return <line key={i} x1={prev.x} y1={prev.y} x2={v.x} y2={v.y} stroke={STROKE} strokeWidth={2} />;
      })}
      {spec.vertices?.map((v, i) => (
        <g key={`v${i}`}>
          <circle cx={v.x} cy={v.y} r={3} fill={STROKE} />
          {v.label && <text x={v.x + 6} y={v.y - 6} fontSize={9} fill={TEXT} fontWeight={700}>{v.label}</text>}
        </g>
      ))}
      <line x1={barX} y1={barY} x2={barX + barLen} y2={barY} stroke={TEXT} strokeWidth={2} />
      <line x1={barX} y1={barY - 4} x2={barX} y2={barY + 4} stroke={TEXT} strokeWidth={2} />
      <line x1={barX + barLen / 2} y1={barY - 3} x2={barX + barLen / 2} y2={barY + 3} stroke={TEXT} strokeWidth={1.5} />
      <line x1={barX + barLen} y1={barY - 4} x2={barX + barLen} y2={barY + 4} stroke={TEXT} strokeWidth={2} />
      <text x={barX + barLen / 2} y={barY + 14} textAnchor="middle" fontSize={8} fill={TEXT_LIGHT}>{barReal.toFixed(1)} {unit}</text>
      <text x={barX + barLen + 8} y={barY + 3} fontSize={8} fill={TEXT_LIGHT}>1:{Math.round(scaleFactor)}</text>
      <text x={SVG_W / 2} y={SVG_H - 38} textAnchor="middle" fontSize={9} fill={TEXT_LIGHT}>Real distance: {realDist.toFixed(2)} {unit}</text>
    </g>
  );
};

// === BEARING JOURNEY ===
// Multi-leg bearing path: origin → Q1 → Q2 → ... with bearing arcs, north arrows, distances, scale bar.
// Automatically computes pixel positions from real bearings + distances.
const BearingJourney = ({ spec }: { spec: DiagramSpec }) => {
  const j = spec.bearingJourney;
  if (!j || j.legs.length === 0) return <text x={SVG_W / 2} y={SVG_H / 2} textAnchor="middle" fontSize={10} fill={TEXT_LIGHT}>No journey data</text>;

  const PX_PER_KM = 7;
  const padX = 50;
  const padY = 50;

  // compute world coords (km, y-up) then convert to pixels
  const worldPts: { x: number; y: number; label: string }[] = [{ x: 0, y: 0, label: j.origin?.label || 'Start' }];
  let wx = 0, wy = 0;
  for (let i = 0; i < j.legs.length; i++) {
    const leg = j.legs[i];
    const rad = (leg.bearing * Math.PI) / 180;
    wx += leg.distance * Math.sin(rad);
    wy += leg.distance * Math.cos(rad);
    const fallbackLabel = String.fromCharCode(65 + i + 1); // B, C, D...
    worldPts.push({ x: wx, y: wy, label: leg.toLabel || (leg as any).label || fallbackLabel });
  }

  // bounds
  const xs = worldPts.map(p => p.x), ys = worldPts.map(p => p.y);
  const minX = Math.min(...xs), maxX = Math.max(...xs);
  const minY = Math.min(...ys), maxY = Math.max(...ys);
  const rangeX = maxX - minX || 1, rangeY = maxY - minY || 1;
  const drawW = SVG_W - 2 * padX, drawH = SVG_H - 2 * padY;
  const fitScale = Math.min(drawW / rangeX, drawH / rangeY);
  const sc = Math.min(fitScale, PX_PER_KM);
  const offsetX = padX + (drawW - rangeX * sc) / 2 - minX * sc;
  const offsetY = padY + (drawH - rangeY * sc) / 2 + maxY * sc;
  const px = (wx2: number) => offsetX + wx2 * sc;
  const py = (wy2: number) => offsetY - wy2 * sc;

  const pts = worldPts.map(p => ({ x: px(p.x), y: py(p.y), label: p.label }));

  // compute real straight-line distance from origin to final point
  const realDist = Math.sqrt(wx * wx + wy * wy);
  const realBearing = ((Math.atan2(wx, wy) * 180) / Math.PI + 360) % 360;

  // scale bar
  const barKm = realDist > 20 ? 10 : realDist > 8 ? 5 : 2;
  const barPx = barKm * sc;
  const barX = SVG_W - barPx - 20;
  const barY = SVG_H - 14;

  // arc helper: small arc at a waypoint showing bearing from North
  const bearingArc = (cx: number, cy: number, bearing: number, r = 18) => {
    const startA = 0; // North
    const endA = bearing;
    const largeArc = bearing > 180 ? 1 : 0;
    const sx2 = cx + r * Math.sin(startA * Math.PI / 180);
    const sy2 = cy - r * Math.cos(startA * Math.PI / 180);
    const ex = cx + r * Math.sin(endA * Math.PI / 180);
    const ey = cy - r * Math.cos(endA * Math.PI / 180);
    return `M ${sx2},${sy2} A ${r},${r} 0 ${largeArc} 1 ${ex},${ey}`;
  };

  return (
    <g>
      {pts.map((p, i) => {
        if (i === 0) return null;
        const prev = pts[i - 1];
        const leg = j.legs[i - 1];
        const dx2 = p.x - prev.x, dy2 = p.y - prev.y;
        const len = Math.sqrt(dx2 * dx2 + dy2 * dy2);
        if (len < 1) return null;
        const ux = dx2 / len, uy = dy2 / len;
        const midX = (prev.x + p.x) / 2;
        const midY = (prev.y + p.y) / 2;
        const labelOff = 12;
        const normX = -uy, normY = ux;
        const lx = midX + normX * labelOff;
        const ly = midY - normY * labelOff;
        return (
          <g key={`leg${i}`}>
            <line x1={prev.x} y1={prev.y} x2={p.x - ux * 8} y2={p.y - uy * 8} stroke={STROKE} strokeWidth={2} />
            <polygon points={`${p.x},${p.y} ${(p.x - ux * 8) + normX * 4},${(p.y - uy * 8) - normY * 4} ${(p.x - ux * 8) - normX * 4},${(p.y - uy * 8) + normY * 4}`} fill={STROKE} />
            <text x={lx} y={ly} textAnchor="middle" fontSize={8} fill={STROKE} fontWeight={600}>{leg.distance} km</text>
          </g>
        );
      })}

      {pts.map((p, i) => {
        const legBearing = i === 0 ? j.legs[0].bearing : j.legs[i - 1].bearing;
        const arcPath = bearingArc(p.x, p.y, legBearing);
        const labelAngle = legBearing / 2;
        const labelR = 26;
        const lx2 = p.x + labelR * Math.sin(labelAngle * Math.PI / 180);
        const ly2 = p.y - labelR * Math.cos(labelAngle * Math.PI / 180);
        return (
          <g key={`wp${i}`}>
            <circle cx={p.x} cy={p.y} r={3.5} fill={STROKE} />
            <text x={p.x - 10} y={p.y + 12} fontSize={10} fill={TEXT} fontWeight={800}>{p.label}</text>
            {i === 0 && (
              <g>
                <line x1={p.x} y1={p.y - 24} x2={p.x} y2={p.y - 38} stroke={TEXT_LIGHT} strokeWidth={1} />
                <polygon points={`${p.x},${p.y - 38} ${p.x - 3},${p.y - 32} ${p.x + 3},${p.y - 32}`} fill={TEXT_LIGHT} />
                <text x={p.x} y={p.y - 42} textAnchor="middle" fontSize={7} fill={TEXT_LIGHT} fontWeight={700}>N</text>
              </g>
            )}
            {i > 0 && (
              <g>
                <line x1={p.x} y1={p.y - 18} x2={p.x} y2={p.y - 28} stroke={TEXT_LIGHT} strokeWidth={0.8} />
                <polygon points={`${p.x},${p.y - 28} ${p.x - 2},${p.y - 23} ${p.x + 2},${p.y - 23}`} fill={TEXT_LIGHT} />
                <text x={p.x} y={p.y - 31} textAnchor="middle" fontSize={6} fill={TEXT_LIGHT} fontWeight={700}>N</text>
                <path d={arcPath} fill="none" stroke="#ef4444" strokeWidth={1.2} />
                <text x={lx2} y={ly2} textAnchor="middle" fontSize={7} fill="#ef4444" fontWeight={700}>{String(legBearing).padStart(3, '0')}°</text>
              </g>
            )}
          </g>
        );
      })}

      {/* dashed return line from last point back to origin */}
      <line x1={pts[pts.length - 1].x} y1={pts[pts.length - 1].y} x2={pts[0].x} y2={pts[0].y} stroke={TEXT_LIGHT} strokeWidth={1} strokeDasharray="4 3" />
      <text x={(pts[0].x + pts[pts.length - 1].x) / 2 + 12} y={(pts[0].y + pts[pts.length - 1].y) / 2 - 8} fontSize={7} fill={TEXT_LIGHT} fontWeight={600}>{realDist.toFixed(1)} km ({String(Math.round(realBearing)).padStart(3, '0')}°)</text>

      {/* scale bar */}
      <line x1={barX} y1={barY} x2={barX + barPx} y2={barY} stroke={TEXT} strokeWidth={2} />
      <line x1={barX} y1={barY - 4} x2={barX} y2={barY + 4} stroke={TEXT} strokeWidth={2} />
      <line x1={barX + barPx} y1={barY - 4} x2={barX + barPx} y2={barY + 4} stroke={TEXT} strokeWidth={2} />
      <text x={barX + barPx / 2} y={barY + 12} textAnchor="middle" fontSize={7} fill={TEXT_LIGHT}>{barKm} km</text>
      <text x={barX + barPx + 4} y={barY + 3} fontSize={6} fill={TEXT_LIGHT}>1:{Math.round(sc)}</text>
    </g>
  );
};

// === BEARING + ELEVATION (3D TRIG) ===
// Top-down view: horizontal bearing from observer to target.
// Side view: elevation angle and true (slant) distance.
const BearingElevation = ({ spec }: { spec: DiagramSpec }) => {
  const bearing = spec.bearing ?? 60;
  const elev = spec.elevation ?? 30;
  const trueDist = spec.trueDistance ?? 10;
  const horizDist = trueDist * Math.cos((elev * Math.PI) / 180);
  const vertDist = trueDist * Math.sin((elev * Math.PI) / 180);
  const cx1 = 100;
  const cy1 = 150;
  const rad = (bearing * Math.PI) / 180;
  const r2 = 80;
  const targetX = cx1 + r2 * Math.sin(rad);
  const targetY = cy1 - r2 * Math.cos(rad);
  const arcR = 24;
  const a1 = rad - Math.PI / 2;
  const a2 = a1 + Math.PI;
  const arcStartX = cx1 + arcR * Math.sin(a2);
  const arcStartY = cy1 - arcR * Math.cos(a2);
  const arcEndX = cx1 + arcR * Math.sin(a1);
  const arcEndY = cy1 - arcR * Math.cos(a1);
  const largeArc = bearing > 180 ? 1 : 0;
  return (
    <g>
      <text x={SVG_W / 2} y={16} textAnchor="middle" fontSize={9} fill={TEXT_LIGHT} fontWeight={700} letterSpacing={1}>3D BEARING + ELEVATION</text>
      <line x1={40} y1={cy1} x2={cx1 - 8} y2={cy1} stroke={TEXT_LIGHT} strokeWidth={1} strokeDasharray="2 2" />
      <line x1={cx1} y1={cy1 - 50} x2={cx1} y2={cy1} stroke={TEXT_LIGHT} strokeWidth={1.2} markerEnd="url(#bevN)" />
      <text x={cx1} y={cy1 - 55} textAnchor="middle" fontSize={8} fill={TEXT_LIGHT} fontWeight={700}>N</text>
      <line x1={cx1} y1={cy1} x2={targetX} y2={targetY} stroke={STROKE} strokeWidth={2} markerEnd="url(#bevB)" />
      <path d={`M ${arcStartX},${arcStartY} A ${arcR},${arcR} 0 ${largeArc} 1 ${arcEndX},${arcEndY}`} fill="none" stroke="#ef4444" strokeWidth={1.2} />
      <text x={cx1 + 30 * Math.sin(rad / 2) + 8} y={cy1 - 30 * Math.cos(rad / 2) + 4} fontSize={8} fill="#ef4444" fontWeight={700}>Bearing {String(bearing).padStart(3, '0')}°</text>
      <text x={targetX + 6} y={targetY} fontSize={8} fill={TEXT} fontWeight={700}>T</text>
      <circle cx={cx1} cy={cy1} r={3} fill={STROKE} />
      <text x={cx1 - 6} y={cy1 + 4} fontSize={8} fill={TEXT} fontWeight={700}>O</text>
      <text x={(cx1 + targetX) / 2 + 10} y={(cy1 + targetY) / 2 - 6} fontSize={8} fill={STROKE} fontWeight={600}>d = {horizDist.toFixed(1)}</text>
      <line x1={SVG_W - 130} y1={cy1 - 50} x2={SVG_W - 130} y2={cy1 + 50} stroke={TEXT_LIGHT} strokeWidth={1} strokeDasharray="2 2" />
      <line x1={SVG_W - 130} y1={cy1} x2={SVG_W - 30} y2={cy1} stroke={STROKE} strokeWidth={2} />
      <line x1={SVG_W - 130} y1={cy1} x2={SVG_W - 30} y2={cy1 - 60} stroke="#ef4444" strokeWidth={2.2} />
      <path d={`M ${SVG_W - 130 + 30},${cy1} A 30,30 0 0 0 ${SVG_W - 130 + 30 * Math.cos((elev * Math.PI) / 180)},${cy1 - 30 * Math.sin((elev * Math.PI) / 180)}`} fill="none" stroke="#ef4444" strokeWidth={1.2} />
      <text x={SVG_W - 130 + 36} y={cy1 - 8} fontSize={8} fill="#ef4444" fontWeight={700}>θ = {elev}°</text>
      <text x={SVG_W - 130 + 70} y={cy1 - 36} fontSize={8} fill="#ef4444" fontWeight={600}>L = {trueDist}</text>
      <text x={SVG_W - 130 + 50} y={cy1 + 12} fontSize={8} fill={STROKE} fontWeight={600}>h = {vertDist.toFixed(1)}</text>
      <defs>
        <marker id="bevN" viewBox="0 0 10 10" refX="5" refY="5" markerWidth="6" markerHeight="6" orient="auto">
          <path d="M 0 0 L 10 5 L 0 10 z" fill={TEXT_LIGHT} />
        </marker>
        <marker id="bevB" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="7" markerHeight="7" orient="auto">
          <path d="M 0 0 L 10 5 L 0 10 z" fill={STROKE} />
        </marker>
      </defs>
    </g>
  );
};

const Shape3DWrapper = ({ children, caption, cameraPos = [3, 3, 3] }: { children: React.ReactNode; caption?: string; cameraPos?: [number, number, number] }) => {
  const ref = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    if (ref.current) {
      const r = ref.current.getBoundingClientRect();
      console.log('[3D] Container measured:', r.width, 'x', r.height);
    }
  });
  return (
    <div className="my-3 bg-white dark:bg-[#0a1024] border border-gray-200 dark:border-white/10 p-3">
      <div ref={ref} className="h-56 w-full min-w-[280px]">
        <Canvas
          camera={{ position: cameraPos, fov: 50 }}
          dpr={[1, 2]}
          style={{ width: '100%', height: '100%' }}
          onCreated={({ gl, scene, camera }) => {
            console.log('[3D] Canvas created. dom:', gl.domElement.width, 'x', gl.domElement.height, 'scene:', scene.children.length, 'cam:', [...camera.position.toArray()].map(v => v.toFixed(1)).join(','));
            gl.setClearColor(0x0a1024, 1);
          }}
        >
          <ambientLight intensity={0.7} />
          <directionalLight position={[5, 5, 5]} intensity={1.2} />
          <directionalLight position={[-5, 3, -2]} intensity={0.6} />
          {children}
          <OrbitControls enablePan={false} />
        </Canvas>
      </div>
      {caption && (
        <p className="text-[10px] text-center font-bold uppercase tracking-widest text-slate-500 dark:text-slate-500 mt-2">
          {caption}
        </p>
      )}
    </div>
  );
};
